import {
  ArrowUpRight,
  Building2,
  CreditCard,
  ShieldCheck,
  Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../hooks/useAuth";
import SettingsCard from "../ui/SettingsCard";
import { formatDateTime, getRoleLabel } from "../ui/settingsUtils";

const GeneralContent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const shortcuts = [
    {
      name: "Expense heads",
      description: "Create and organise categories for income and expenses.",
      path: "/admin/settings/expense-head",
      icon: CreditCard,
    },
    {
      name: "User roles",
      description: "Control who can view, create, and manage fleet records.",
      path: "/admin/users",
      icon: Users,
    },
  ];

  const accountFacts = [
    { label: "Account type", value: user?.userType || "Admin" },
    { label: "Role", value: getRoleLabel(user?.roles, user?.userType) },
    { label: "Status", value: user?.isActive ? "Active" : "Inactive" },
    { label: "Joined", value: formatDateTime(user?.createdOn) },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {shortcuts.map((item) => {
          const Icon = item.icon;
          return (
            <button
              key={item.name}
              type="button"
              onClick={() => navigate(item.path)}
              className="group flex items-start justify-between gap-4 rounded-2xl border border-[#EEEFEA] bg-white p-5 text-left shadow-[0_1px_2px_rgba(28,28,28,0.04)] transition-all hover:-translate-y-0.5 hover:border-primary/20 hover:shadow-md"
            >
              <div className="flex items-start gap-4">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Icon size={20} />
                </span>
                <div>
                  <p className="text-[15px] font-semibold text-hColor">
                    {item.name}
                  </p>
                  <p className="mt-1 text-[13px] leading-relaxed text-pColor">
                    {item.description}
                  </p>
                </div>
              </div>
              <ArrowUpRight
                size={18}
                className="mt-1 shrink-0 text-pColor transition-colors group-hover:text-primary"
              />
            </button>
          );
        })}
      </div>

      <SettingsCard
        title="Account overview"
        description="A snapshot of this workspace account."
      >
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {accountFacts.map((fact) => (
            <div
              key={fact.label}
              className="rounded-xl border border-[#F1F1EE] bg-[#F8F8F5] px-4 py-4"
            >
              <p className="text-[12px] font-medium text-pColor">{fact.label}</p>
              <p className="mt-1 text-sm font-semibold text-hColor">
                {fact.value}
              </p>
            </div>
          ))}
        </div>
        <div className="mt-5 flex items-center gap-3 rounded-xl bg-primary/[0.06] px-4 py-3">
          <Building2 size={18} className="text-primary" />
          <p className="text-[13px] text-hColor">
            Company-wide details such as expense heads live in the shortcuts
            above.
          </p>
        </div>
      </SettingsCard>

      <SettingsCard
        title="Workspace access"
        description="Need someone else to help run the fleet?"
      >
        <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#EDF2DF] text-primary">
              <ShieldCheck size={18} />
            </span>
            <div>
              <p className="text-sm font-semibold text-hColor">
                Invite or manage staff
              </p>
              <p className="text-[13px] text-pColor">
                Add users and assign roles from user management.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate("/admin/users")}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
          >
            Open users
          </button>
        </div>
      </SettingsCard>
    </div>
  );
};

export default GeneralContent;
